/**
 * Maintenance event bus — the HTTP layer tells the React tree "the platform is
 * in maintenance" (a 503 with code MAINTENANCE_MODE) without importing React.
 *
 * ⚠️ e2e 2026-09-24: that 503 was read as a dead session. The customer bounced
 * between /login and an empty shell and never saw the super admin's message.
 * Same shape as `session-events`: module-level listeners, fire-and-forget.
 */

const MAINTENANCE_MODE_CODE = 'MAINTENANCE_MODE'

export interface MaintenanceNotice {
  message: string
  scheduledEnd: string | null
}

type MaintenanceListener = (notice: MaintenanceNotice) => void

const listeners = new Set<MaintenanceListener>()

export function subscribeToMaintenance(listener: MaintenanceListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function emitMaintenance(notice: MaintenanceNotice): void {
  for (const listener of listeners) {
    try {
      listener(notice)
    } catch {
      // Listeners must not break the HTTP error path.
    }
  }
}

type RecheckListener = () => void
const recheckListeners = new Set<RecheckListener>()

/** Ask the gate to re-read the public status now (after the panel changed it). */
export function requestMaintenanceRecheck(): void {
  for (const listener of recheckListeners) {
    try {
      listener()
    } catch {
      // ignore
    }
  }
}

export function subscribeToMaintenanceRecheck(listener: RecheckListener): () => void {
  recheckListeners.add(listener)
  return () => {
    recheckListeners.delete(listener)
  }
}

/** True for the backend's maintenance refusal (503 + MAINTENANCE_MODE). */
export function isMaintenanceRefusal(error: unknown): boolean {
  const e = error as { status?: number; code?: string } | null
  return e?.status === 503 && e.code === MAINTENANCE_MODE_CODE
}

/** Pull the notice out of a maintenance refusal's error payload. */
export function noticeFromRefusal(error: {
  message?: string
  details?: unknown
}): MaintenanceNotice {
  const details = (error.details ?? {}) as { scheduledEnd?: unknown }
  return {
    message: error.message ?? '',
    scheduledEnd: typeof details.scheduledEnd === 'string' ? details.scheduledEnd : null,
  }
}
